import { signal } from '@preact/signals-react'
import type { Clerk } from '@clerk/clerk-js'
import type { AuthDep } from './auth.types'

function parsePublishableKey(key: string): string | null {
  const parts = key.split('_')
  if (parts.length < 3) return null
  const base64Part = parts[2]
  try {
    const decoded = atob(base64Part)
    return decoded.endsWith('$') ? decoded.slice(0, -1) : decoded
  } catch (e) {
    return null
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.crossOrigin = 'anonymous'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.body.appendChild(script)
  })
}

async function loadClerkAndUI(publishableKey: string): Promise<Clerk> {
  if (typeof window === 'undefined') {
    throw new Error('Cannot load Clerk script on server side')
  }

  const frontendApi = parsePublishableKey(publishableKey)
  if (!frontendApi) {
    throw new Error('Invalid Clerk Publishable Key')
  }

  // Load the Clerk core JS script if not already on window
  if (!(window as any).Clerk) {
    ;(window as any).__clerk_publishable_key = publishableKey
    const clerkScriptUrl = `https://${frontendApi}/npm/@clerk/clerk-js@6/dist/clerk.browser.js`
    await loadScript(clerkScriptUrl)
  }

  // Load the Clerk UI components script if not already on window
  if (!(window as any).__internal_ClerkUICtor) {
    const uiScriptUrl = `https://${frontendApi}/npm/@clerk/ui@1/dist/ui.browser.js`
    await loadScript(uiScriptUrl)
  }

  const clerkInstance = (window as any).Clerk
  if (!clerkInstance) {
    throw new Error('Clerk was not initialized on window after loading scripts')
  }

  return clerkInstance
}

export function makeAuthDep(): AuthDep {
  const currentUser = signal<any>(null)
  const isLoaded = signal<boolean>(false)

  if (typeof window === 'undefined') {
    return {
      currentUser,
      isLoaded,
      signIn: async () => {},
      signUp: async () => {},
      signOut: async () => {},
      openUserProfile: () => {},
      getToken: async () => null,
      setProfileImage: async () => {},
      _clerk: null,
    }
  }

  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
  if (!publishableKey) {
    console.warn('[Clerk Auth] VITE_CLERK_PUBLISHABLE_KEY is not defined. Authentication will not function.')
    return {
      currentUser,
      isLoaded,
      signIn: async () => { console.error('Clerk key missing') },
      signUp: async () => { console.error('Clerk key missing') },
      signOut: async () => {},
      openUserProfile: () => {},
      getToken: async () => null,
      setProfileImage: async () => {},
      _clerk: null,
    }
  }

  let clerkInstance: Clerk | null = null

  // Asynchronously load Clerk + UI via script tags and hook up listener
  loadClerkAndUI(publishableKey)
    .then((clerk) => {
      clerkInstance = clerk
      const uiCtor = (window as any).__internal_ClerkUICtor
      return clerk.load({
        clerkUICtor: uiCtor,
        ui: {
          ClerkUI: uiCtor
        }
      } as any).then(() => {
        currentUser.value = clerk.user || null
        isLoaded.value = true

        clerk.addListener((state) => {
          currentUser.value = state.user || null
        })
      })
    })
    .catch((err) => {
      console.error('[Clerk Auth] Failed to load Clerk client:', err)
      isLoaded.value = true // mark loaded so UI doesn't spin forever, but user is null
    })

  return {
    currentUser,
    isLoaded,
    signIn: async () => {
      if (clerkInstance) {
        await clerkInstance.openSignIn({
          forceRedirectUrl: window.location.href,
        })
      } else {
        console.error('Clerk not loaded')
      }
    },
    signUp: async () => {
      if (clerkInstance) {
        await clerkInstance.openSignUp({
          forceRedirectUrl: window.location.href,
        })
      } else {
        console.error('Clerk not loaded')
      }
    },
    signOut: async () => {
      if (clerkInstance) {
        await clerkInstance.signOut({
          redirectUrl: window.location.href,
        })
      }
    },
    openUserProfile: () => {
      if (clerkInstance) {
        clerkInstance.openUserProfile()
      }
    },
    getToken: async () => {
      if (!clerkInstance || !clerkInstance.session) return null
      return clerkInstance.session.getToken()
    },
    setProfileImage: async (file: File) => {
      if (!clerkInstance?.user) {
        console.error('Cannot set profile image: not signed in')
        return
      }
      await clerkInstance.user.setProfileImage({ file })
    },
    get _clerk() {
      return clerkInstance
    },
  }
}
